import React from "react";
import { DoubleArrow } from "@mui/icons-material";
import {
  Typography,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { themes } from "services/constants";

import {
  doubleArrowStyle,
  parentBoxStyle,
  childBoxStyleSpaceBetween,
  stickyHeader,
} from "services/constants";
import { infoStyle } from "services/constants";
import { useSelector } from "react-redux";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";

const DataTable = ({
  data,
  columns,
  emptyMessage = "No data available",
  renderRow,
  summaryRow,
  showAddWhenEmpty = false,
}) => {
  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  return (
    <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor, mb: 2 }}>
      <Table size="small" className="border">
        <TableHead sx={stickyHeader}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={index}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        {safeData.length === 0 ? (
          <TableBody>
            {showAddWhenEmpty ? (
              renderRow(null, 0, true)
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Alert severity="info">{emptyMessage}</Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        ) : (
          <TableBody>
            {safeData.map((item, index) => {
              if (!item) return null;
              return renderRow(item, index);
            })}
            {summaryRow && summaryRow()}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export const ViewUserComponentDetails = ({ onClose, userData }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const { result: templateData, loading } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/users-component/?user_id=${userData?.user_id}`
  );

  const earnings = Array.isArray(templateData?.earnings)
    ? templateData.earnings
    : [];

  const deductions = Array.isArray(templateData?.deductions)
    ? templateData.deductions
    : [];

  const employerContributions = Array.isArray(
    templateData?.employer_contribution
  )
    ? templateData.employer_contribution
    : [];

  const reimbursements = Array.isArray(templateData?.reimbursement)
    ? templateData.reimbursement
    : [];

  const SectionHeader = ({ children }) => (
    <Typography sx={{ fontWeight: 600 }}>{children}</Typography>
  );

  const SubHeader = ({ children }) => (
    <Typography {...infoStyle}>{children}</Typography>
  );

  return loading ? (
    <Loader />
  ) : (
    <>
      <Box sx={parentBoxStyle}>
        <Paper elevation={0} sx={{ borderRadius: 0 }}>
          <Box>
            <Box sx={childBoxStyleSpaceBetween}>
              <IconButton onClick={onClose} sx={doubleArrowStyle}>
                <DoubleArrow />
              </IconButton>
            </Box>

            <Box sx={{ ml: 2.2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {"Salary Component"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View your predefined component structure that can be applied to
                employees.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <UserDetailsRow
              userDetails={{
                image_url: templateData?.user?.image_url,
                employee_id: templateData?.user?.employee_id,
                user_name: templateData?.user?.employee_name,
                department_name: templateData?.user?.department,
                designation: templateData?.user?.designation,
              }}
            />
          </Box>
          {/* Earnings Section */}
          {earnings.length > 0 && (
            <>
              <SectionHeader>Earnings</SectionHeader>
              <SubHeader>
                Components that contribute to employee earnings (Basic, HRA,
                Bonus, etc.)
              </SubHeader>
              <DataTable
                data={earnings}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component ?? "-"}</TableCell>
                    <TableCell>{data?.method ?? "-"}</TableCell>
                    <TableCell>{data?.computation ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Earnings</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>₹{templateData?.total_earnings ?? 0}</strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}

          {/* Deductions Section */}
          {deductions.length > 0 && (
            <>
              <SectionHeader>Deductions</SectionHeader>
              <SubHeader>
                Components that are deducted from earnings (TDS, PF, Loan, etc.)
              </SubHeader>
              <DataTable
                data={deductions}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component ?? "-"}</TableCell>
                    <TableCell>{data?.method ?? "-"}</TableCell>
                    <TableCell>{data?.computation ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Deductions</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>₹{templateData?.total_deductions ?? 0}</strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}

          {/* Employer Contribution Section */}
          {employerContributions.length > 0 && (
            <>
              <SectionHeader>Employer Contribution</SectionHeader>
              <SubHeader>
                Statutory or custom employer contributions
                (PF,ESI,Insurance,etc.)
              </SubHeader>
              <DataTable
                data={employerContributions}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component ?? "-"}</TableCell>
                    <TableCell>{data?.method ?? "-"}</TableCell>
                    <TableCell>{data?.computation ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Contribution</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>
                        ₹{templateData?.total_employer_contribution ?? 0}
                      </strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}

          {/* Reimbursement Section */}
          {reimbursements.length > 0 && (
            <>
              <SectionHeader>Reimbursement</SectionHeader>
              <SubHeader>
                Reimbursement components for employee expenses
                (Travel,Food,Medical, etc.)
              </SubHeader>
              <DataTable
                data={reimbursements}
                columns={["Component", "Method", "Computation", "Total"]}
                renderRow={(data, index) => (
                  <TableRow key={index}>
                    <TableCell>{data?.component ?? "-"}</TableCell>
                    <TableCell>{data?.method ?? "-"}</TableCell>
                    <TableCell>{data?.computation ?? "0"}</TableCell>
                    <TableCell>{data?.amount ?? "0"}</TableCell>
                  </TableRow>
                )}
                summaryRow={() => (
                  <TableRow>
                    <TableCell>
                      <strong>Total Reimbursement</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell>
                      <strong>₹{templateData?.total_reimbursement ?? 0}</strong>
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};
